import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Message } from '../types';
import { CopyAll as CopyAllIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
//import { Button } from './Buttons';

function parseMarkdownByH2AndH3(markdownText: string | undefined) {
  if (!markdownText) return [];
  const lines = markdownText.split('\n');
  const sections: any[] = [];
  let currentSection: any = null;
  let currentSubsection: any = null;

  lines.forEach((line) => {
    if (line.trim().startsWith('## ')) {
      if (currentSection) {
        if (currentSubsection) {
          currentSection.subsections.push(currentSubsection);
          currentSubsection = null;
        }
        sections.push(currentSection);
      }
      currentSection = {
        heading: line.trim().replace('## ', ''),
        subsections: [],
      };
    } else if (currentSection && line.trim().startsWith('### ')) {
      if (currentSubsection) {
        currentSection.subsections.push(currentSubsection);
      }
      currentSubsection = {
        type: 'normal',
        heading: line.trim().replace('### ', ''),
        content: [],
      };
    } else if (currentSection && line.match(/\[(VAR-prosedyrer)\]\(.+\)/)) {
      if (currentSubsection) {
        currentSection.subsections.push(currentSubsection);
      }
      const urlMatch = line.match(/mainSearchCriteria\.v\.c=([0-9]+)/);
      const conceptId = urlMatch ? urlMatch[1] : null;
      currentSubsection = {
        type: 'var-prosedyrer-button',
        headingText: 'VAR-prosedyrer',
        conceptId: conceptId,
        content: [],
      };
    } else if (currentSection && currentSubsection) {
      currentSubsection.content.push(line);
    }
  });

  if (currentSection) {
    if (currentSubsection) {
      currentSection.subsections.push(currentSubsection);
    }
    sections.push(currentSection);
  }
  return sections;
}

interface VarProcedure {
  id: string;
  title: string;
  link: string;
}

interface VarProcedureData {
  procedures: VarProcedure[] | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  checked: { [procId: string]: boolean };
}

type VismaCarePlanMessageProps = {
  message: Message;
  fetchProcedureInfo: (conceptId: string) => Promise<VarProcedure[]>;
  preloadedProcedures?: { [conceptId: string]: VarProcedure[] }; // Ny prop for forhåndslastede data
};

export const VismaCarePlanMessage: React.FC<VismaCarePlanMessageProps> = ({
  message,
  fetchProcedureInfo,
  preloadedProcedures = {},
}) => {
  const [parsedSections, setParsedSections] = useState<any[]>([]);
  const [checkedSubsections, setCheckedSubsections] = useState<{ [key: string]: string }>({});
  const [varProcedureState, setVarProcedureState] = useState<{ [conceptId: string]: VarProcedureData }>({});

  useEffect(() => {
    if (message.text) {
      const sections = parseMarkdownByH2AndH3(message.text);
      console.log('Parsed sections:', sections);
      setParsedSections(sections);

      // Pre-load prosedyrer fra cache hvis tilgjengelig
      sections.forEach((section) => {
        section.subsections.forEach((subsection: any) => {
          if (
            subsection.type === 'var-prosedyrer-button' &&
            subsection.conceptId &&
            preloadedProcedures[subsection.conceptId]
          ) {
            setVarProcedureState((prev) => ({
              ...prev,
              [subsection.conceptId]: {
                ...prev[subsection.conceptId],
                procedures: preloadedProcedures[subsection.conceptId],
                isLoading: false,
                isOpen: false,
                error: null,
                checked: {},
              },
            }));
          }
        });
      });
    }
  }, [message.text, preloadedProcedures]);

  const handleSubsectionCheck = useCallback((sectionIdx: number, subIdx: number, subsection: any, isChecked: boolean) => {
    const key = `${sectionIdx}-${subIdx}`;
    console.log(`Updating checkbox for key: ${key}, checked: ${isChecked}`);
    setCheckedSubsections((prev) => {
      const newState = { ...prev };
      if (isChecked) {
        newState[key] = `${subsection.heading}\n${subsection.content.join('\n')}`;
      } else {
        delete newState[key];
      }
      console.log('New checkedSubsections:', newState);
      return newState;
    });
  }, []);

  const toggleVarProcedures = useCallback(
    async (conceptId: string | null) => {
      if (!conceptId) return;

      setVarProcedureState((prev) => {
        const current = prev[conceptId] || {
          procedures: null,
          isOpen: false,
          isLoading: false,
          error: null,
          checked: {},
        };
        // Bruk preloaded data hvis tilgjengelig, ellers start lasting
        const procedures = preloadedProcedures[conceptId] || current.procedures;
        return {
          ...prev,
          [conceptId]: {
            ...current,
            isOpen: !current.isOpen,
            isLoading: !procedures && !current.isOpen,
            procedures: procedures || null,
            error: null,
          },
        };
      });

      if (
        !varProcedureState[conceptId]?.procedures &&
        !varProcedureState[conceptId]?.isOpen &&
        !preloadedProcedures[conceptId]
      ) {
        try {
          const procedures = await fetchProcedureInfo(conceptId);
          console.log('Fetched procedures:', procedures);
          setVarProcedureState((prev) => ({
            ...prev,
            [conceptId]: {
              ...prev[conceptId],
              procedures: procedures,
              isLoading: false,
            },
          }));
        } catch (err) {
          console.error('Failed to fetch VAR procedures', err);
          setVarProcedureState((prev) => ({
            ...prev,
            [conceptId]: {
              ...prev[conceptId],
              isLoading: false,
              error: 'Kunne ikke laste prosedyrer.',
            },
          }));
        }
      }
    },
    [fetchProcedureInfo, varProcedureState, preloadedProcedures]
  );

  const handleVarProcedureCheck = useCallback((conceptId: string, procId: string, isChecked: boolean) => {
    if (!conceptId) return;
    setVarProcedureState((prev) => {
      const newState = {
        ...prev,
        [conceptId]: {
          ...prev[conceptId],
          checked: {
            ...prev[conceptId]?.checked,
            [procId]: isChecked,
          },
        },
      };
      console.log('New varProcedureState:', newState);
      return newState;
    });
  }, []);

  const handleCopySelection = useCallback(() => {
    const parts: string[] = [];

    for (const text of Object.values(checkedSubsections)) {
      parts.push(text);
    }

    Object.values(varProcedureState).forEach((data) => {
      if (data.procedures && data.checked) {
        data.procedures.forEach((proc) => {
          if (data.checked[proc.id]) {
            parts.push(proc.title);
          }
        });
      }
    });

    const selectedText = parts.join('\n\n');

    console.log('Copying selected text:', selectedText);

    if (selectedText.trim()) {
      navigator.clipboard.writeText(selectedText.trim());
    }
  }, [checkedSubsections, varProcedureState]);

  const handleCopyAll = useCallback(() => {
    if (message.text) {
      navigator.clipboard.writeText(message.text);
    }
  }, [message.text]);

  const memoizedSections = useMemo(() => parsedSections, [parsedSections]);

  if (!memoizedSections.length) {
    return (
      <div className="p-4 prose dark:prose-invert">
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {message.text || ''}
        </Markdown>
      </div>
    );
  }



return (
  <div className="p-4 my-2 rounded-lg">
    {memoizedSections.map((section, sectionIdx) => (
      <div key={sectionIdx} className="mb-6">
        <div className="ml-4 mr-4 border-b border-gray-300 pb-1 mb-4">
          <h2 className="text-2xl font-semibold text-[#10756C] tracking-wide font-sans">
            {section.heading.toUpperCase()}
          </h2>
        </div>
        {section.subsections.map((subsection: any, subIdx: number) => {
          const uniqueKey = `${sectionIdx}-${subIdx}`;
          const isVar = subsection.type === 'var-prosedyrer-button' && subsection.conceptId;

          return (
            <div key={uniqueKey} className="ml-4 mb-6 max-w-[calc(100%-2rem)]">
              {!isVar ? (
                <>
                  <label className="flex items-center space-x-2 mb-1">
                    <input
                      type="checkbox"
                      className="form-checkbox h-5 w-5 border-gray-300 rounded"
                      style={{ accentColor: '#42BFB0' }}
                      checked={!!checkedSubsections[uniqueKey]}
                      onChange={(e) => handleSubsectionCheck(sectionIdx, subIdx, subsection, e.target.checked)}
                    />
                    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
                      {subsection.heading}
                    </h3>
                  </label>
                  <div className="ml-7 prose prose-sm dark:prose-invert max-w-none mb-0.5">
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {subsection.content.join('\n')}
                    </Markdown>
                  </div>
                </>
              ) : (
                <div className="ml-7 -mt-2 px-4 py-3 bg-gray-200 rounded-md">
                  <button
                    onClick={() => toggleVarProcedures(subsection.conceptId)}
                    className="flex justify-between items-center w-full text-left font-medium text-sm text-[#10756C]"
                    disabled={varProcedureState[subsection.conceptId]?.isLoading}
                  >
                    <span>{subsection.headingText || 'VAR-prosedyrer'}</span>
                    {varProcedureState[subsection.conceptId]?.isOpen ? <ExpandLess /> : <ExpandMore />}
                  </button>
                  {varProcedureState[subsection.conceptId]?.isOpen && (
                    <div className="mt-2 pl-2">
                      {varProcedureState[subsection.conceptId]?.isLoading && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Laster prosedyrer...</p>
                      )}
                      {varProcedureState[subsection.conceptId]?.error && (
                        <p className="text-sm text-red-500">
                          {varProcedureState[subsection.conceptId]?.error}
                        </p>
                      )}
                      {varProcedureState[subsection.conceptId]?.procedures?.map((proc) => (
                        <label key={proc.id} className="flex items-center space-x-2 py-1 text-sm">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 border-gray-300 rounded"
                            style={{ accentColor: '#42BFB0' }}
                            checked={!!varProcedureState[subsection.conceptId]?.checked[proc.id]}
                            onChange={(e) => handleVarProcedureCheck(subsection.conceptId, proc.id, e.target.checked)}
                          />
                          <img
                            src="/icons/varnett.png"
                            alt="VAR logo"
                            style={{ width: '20px', height: '20px' }}
                          />
                          <a
                            href={proc.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline text-[#10756C]"
                          >
                            {proc.title}
                          </a>
                        </label>
                      ))}
                      {varProcedureState[subsection.conceptId]?.procedures?.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Ingen prosedyrer funnet.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ))}
    <div className="mt-4 pt-3 border-t dark:border-gray-600 flex items-center space-x-2">
      <button
        onClick={handleCopySelection}
        className="bg-gray-200 text-black hover:bg-gray-300 active:scale-95 transition-transform px-4 py-2 rounded text-sm font-medium flex items-center"
      >
        <CopyAllIcon fontSize="small" className="mr-2" /> Kopier utvalg
      </button>
      <button
        onClick={handleCopyAll}
        className="bg-gray-200 text-black hover:bg-gray-300 active:scale-95 transition-transform px-4 py-2 rounded text-sm font-medium flex items-center"
      >
        <CopyAllIcon fontSize="small" className="mr-2" /> Kopier alt
      </button>
    </div>
  </div>
);



};